import React, { useState } from 'react';
import { signInWithGoogle, signInWithGoogleRedirect, sendSignInLink } from '../services/firebase';
import './SignupModal.css';

function SignupModal({ onClose, onSignup }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [isLogin, setIsLogin] = useState(false);

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    
    try {
      await sendSignInLink(email);
      setEmailLinkSent(true);
    } catch (err) {
      console.error('Email link error:', err);
      setError(err.message || 'Error sending sign-in link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    
    try {
      const user = await signInWithGoogle();
      // Store user info in localStorage
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('userId', user.uid);
      
      // Call the onSignup callback
      onSignup(user.email);
      
      // Force a page refresh to update all components
      window.location.reload();
    } catch (err) {
      console.error('Google sign-in error:', err);
      
      // If the error is related to popups, try the redirect method
      if (err.code && (
        err.code === 'auth/popup-blocked' || 
        err.code === 'auth/popup-closed-by-user' ||
        err.code === 'auth/configuration-not-found'
      )) {
        try {
          // Let the user know we're trying an alternative method
          setError('Trying alternative sign-in method...');
          await signInWithGoogleRedirect();
          // This will redirect, so the following code won't execute
        } catch (redirectErr) {
          setError(redirectErr.message || 'Error signing in with Google. Please try again.');
        }
      } else {
        setError(err.message || 'Error signing in with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // If email link was sent, show confirmation message
  if (emailLinkSent) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2>Check Your Email</h2>
          <p>
            We've sent a sign-in link to <strong>{email}</strong>. 
            Click the link in the email to sign in.
          </p>
          <p className="email-note">
            The link will expire after 24 hours. If you don't see the email, 
            check your spam folder.
          </p>
          <button 
            onClick={onClose} 
            className="close-button"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Create an Account</h2>
          <p>Sign up to save your calculation history and access additional features!</p>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="social-login">
          <button 
            type="button" 
            onClick={handleGoogleSignIn} 
            className="google-button"
            disabled={loading}
          >
            <svg className="google-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            <span className="google-button-text">
              {isLogin ? 'Log in with Google' : 'Sign up with Google'}
            </span>
          </button>
        </div>
        
        <div className="separator">
          <span>or</span>
        </div>
        
        <form onSubmit={handleEmailSignIn}>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email address"
            />
          </div>
          
          <div className="modal-buttons">
            <button type="submit" disabled={loading}>
              {loading ? 'Sending Link...' : 'Send Sign-in Link'}
            </button>
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
          </div>
        </form>
        
        <div className="login-options">
          <p>
            {isLogin 
              ? "Don't have an account? " 
              : "Already have an account? "}
            <a href="#" onClick={(e) => {
              e.preventDefault();
              setIsLogin(!isLogin);
              setError('');
            }}>
              {isLogin ? 'Sign Up' : 'Log In'}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupModal; 